/**
 * A room's Page and offer tab: the sign-up page as visitors see it now, in a browser frame, with its visits and
 * how many registered; what it asks for and the confirmation email; and the offer.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import type { OfferJson, WebinarDetail } from "../../../shared/types";
import { boardPath } from "../../board/paths";
import { Button } from "../../components/ui/Button";
import { Field } from "../../components/ui/Field";
import { Icon } from "../../components/ui/Icon";
import { Pill } from "../../components/ui/Pill";
import { Sheet } from "../../components/ui/Sheet";
import { Toggle } from "../../components/ui/Toggle";
import { useToast } from "../../components/ui/Toast";
import { ApiError } from "../../lib/api";
import { type OfferChange, offerLinks, offerProblem, saveOffer } from "./offerEdit";
import { linkWords, usePageDoc, usePageTab } from "./usePageTab";

const n = (v: number) => v.toLocaleString("en-US");
const CANVAS = 880;
/** The page is drawn as the top of a laptop screen, which is what a visitor first sees. */
const CANVAS_H = 620;

/** The page as a visitor sees it now, drawn small. */
function PagePreview({ detail }: { detail: WebinarDetail }) {
  const [scale, setScale] = useState(0.6);
  const fit = useRef<HTMLDivElement>(null);
  const doc = usePageDoc(detail);

  useEffect(() => {
    const el = fit.current;
    if (!el) return;
    const size = () => setScale(el.clientWidth / CANVAS);
    size();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(size);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="pvfit" ref={fit} style={{ height: (CANVAS_H + 38) * scale }}>
      <div className="pvcanvas" style={{ width: CANVAS, transform: `scale(${scale})` }}>
        <div className="browser">
          <div className="bb">
            <i />
            <i />
            <i />
            <div className="url">
              <Icon name="lock" />
              <span>{detail.register_url.replace(/^https?:\/\//, "")}</span>
            </div>
          </div>
          {doc ? (
            <iframe title="The sign-up page" srcDoc={doc} sandbox="" scrolling="no" tabIndex={-1} aria-hidden="true" style={{ height: CANVAS_H }} />
          ) : (
            <div className="pvwait" style={{ height: CANVAS_H }} />
          )}
        </div>
      </div>
    </div>
  );
}

/** The offer's button, where it goes and when it appears, changed in place. */
export function OfferSheet({
  roomId,
  hasBoard,
  offer,
  onClose,
  onSaved,
}: {
  roomId: string;
  hasBoard: boolean;
  offer: OfferJson;
  onClose: () => void;
  onSaved: (offer: OfferJson) => void;
}) {
  const toast = useToast();
  const [change, setChange] = useState<OfferChange>({
    label: offer.label,
    url: offer.url ?? "",
    minute: Math.round((offer.appears_at ?? 0) / 60),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const links = offerLinks(offer.kind);
  const save = () => {
    const problem = offerProblem(offer.kind, change);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError("");
    saveOffer(roomId, hasBoard, offer, change)
      .then(() => {
        onSaved({ ...offer, label: change.label.trim(), ...(links ? { url: change.url.trim() } : {}), appears_at: change.minute * 60 });
        toast("The offer is saved.");
        onClose();
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : "That didn't save. Try again."))
      .finally(() => setBusy(false));
  };
  return (
    <Sheet
      open
      onClose={onClose}
      title="The offer"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" disabled={busy} onClick={save}>
            {busy ? "Saving…" : "Save the offer"}
          </Button>
        </>
      }
    >
      <div className="col" style={{ gap: 14 }}>
        <Field label="Button" htmlFor="ofbtn">
          <input dir="auto" id="ofbtn" className="inp" value={change.label} maxLength={60} onChange={(e) => setChange({ ...change, label: e.target.value })} />
        </Field>
        {links ? (
          <Field label="Where it goes" htmlFor="ofurl">
            <input dir="auto" id="ofurl" className="inp" value={change.url} inputMode="url" onChange={(e) => setChange({ ...change, url: e.target.value })} />
          </Field>
        ) : null}
        <Field label="Appears at, minutes in" htmlFor="ofmin" error={error || undefined}>
          <input
            id="ofmin"
            className="inp"
            type="number"
            min={0}
            value={Number.isFinite(change.minute) ? change.minute : ""}
            onChange={(e) => setChange({ ...change, minute: e.target.value === "" ? Number.NaN : Number(e.target.value) })}
          />
        </Field>
      </div>
    </Sheet>
  );
}

export function PageTab({ detail, onChanged }: { detail: WebinarDetail; onChanged: () => void }) {
  const navigate = useNavigate();
  const { visits, registered, rate, phone, fixed, extra, confirm, busy, asking, flipPhone, flipConfirm, offer, setOffer, editing, setEditing } =
    usePageTab(detail, onChanged);
  const editPage = () => navigate(boardPath(detail.id));

  return (
    <div className="grid ptgrid">
      <section className="card">
        <div className="card-h">
          <div>
            <h2>The sign-up page</h2>
          </div>
          <div className="row g6">
            {detail.board ? (
              <Button size="s" icon="pencil" onClick={editPage}>
                Edit the page
              </Button>
            ) : null}
            {detail.status === "published" ? (
              <Button size="s" icon="eye" onClick={() => window.open(detail.register_url, "_blank", "noopener")}>
                Preview
              </Button>
            ) : null}
          </div>
        </div>
        <div className="card-b">
          <PagePreview detail={detail} />
          <div className="stat4 stat3 mt20">
            <div className="stat">
              <b>{n(visits)}</b>
              <span>Visits</span>
            </div>
            <div className="stat">
              <b>{n(registered)}</b>
              <span>Registered</span>
            </div>
            <div className="stat">
              <b>{rate}%</b>
              <span>of visitors registered</span>
            </div>
          </div>
        </div>
      </section>
      <div className="col" style={{ gap: 20 }}>
        <section className="card">
          <div className="card-h">
            <h2>Registration</h2>
          </div>
          <div className="card-b col" style={{ gap: 12 }}>
            {fixed.map((f) => (
              <div key={f.key} className="row">
                <div className="sp">
                  <div className="b6">{f.label}</div>
                </div>
                <Pill tone="line" icon="lock">
                  Always asked
                </Pill>
              </div>
            ))}
            <div className="row">
              <div className="sp">
                <div className="b6">Phone</div>
              </div>
              <Toggle on={phone} onChange={flipPhone} label="Phone" disabled={asking} />
            </div>
            {extra.map((f) => (
              <div key={f.key} className="row">
                <div className="sp">
                  <div className="b6">{f.label}</div>
                </div>
                <Pill tone="line">{f.required ? "Required" : "Asked"}</Pill>
              </div>
            ))}
            <div className="divider" style={{ margin: "4px 0" }} />
            <div className="row">
              <div className="sp">
                <div className="b6">Confirmation email</div>
              </div>
              <Toggle on={confirm} onChange={flipConfirm} label="Confirmation email" disabled={busy} />
            </div>
          </div>
        </section>
        <section className="card">
          <div className="card-h">
            <h2>The offer</h2>
            {offer && offer.label ? (
              <Button size="s" icon="pencil" onClick={() => setEditing(true)}>
                Edit
              </Button>
            ) : null}
          </div>
          <div className="card-b">
            {offer && offer.label ? (
              <dl className="kv" style={{ margin: 0 }}>
                <dt>Button</dt>
                <dd>
                  <Pill tone="wine">{offer.label}</Pill>
                </dd>
                {offer.url ? (
                  <>
                    <dt>Where it goes</dt>
                    <dd className="kvurl">{linkWords(offer.url)}</dd>
                  </>
                ) : null}
                {offer.appears_at !== undefined ? (
                  <>
                    <dt>Appears at</dt>
                    <dd>{Math.round(offer.appears_at / 60)} min, stays to the end</dd>
                  </>
                ) : null}
                <dt>Clicked</dt>
                <dd>
                  {n(detail.chain.clicked)} {detail.chain.clicked === 1 ? "person" : "people"}, {n(detail.chain.customers)} became{" "}
                  {detail.chain.customers === 1 ? "a customer" : "customers"}
                </dd>
              </dl>
            ) : (
              <p className="small muted">No offer on this room yet.</p>
            )}
          </div>
        </section>
      </div>
      {editing && offer ? (
        <OfferSheet
          roomId={detail.id}
          hasBoard={detail.board}
          offer={offer}
          onClose={() => setEditing(false)}
          onSaved={(saved) => {
            setOffer(saved);
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}
